import { CountOperator, getRelationshipDirection } from '@reach/shared-types/conditions';
import { generateId } from '@/lib/generate-id';
import React from 'react';
import { getRelationshipOptionsForSubject } from '../../engage-segments-tab/segment-definitions-api';
import type { SchemaRelationship } from '../../engage-segments-tab/types';
import type { BuilderRelatedResourceCondition } from './segment-builder.types';
import { useSegmentSchemaCatalog } from './useSegmentSchemaCatalog';
import styles from './SegmentVisualBuilder.module.css';

interface AddRelatedResourceConditionProps {
  subjectSchemaId: string | null;
  addRelatedResourceCondition: (condition: BuilderRelatedResourceCondition) => void;
  disabled?: boolean;
}

export const AddRelatedResourceCondition: React.FC<AddRelatedResourceConditionProps> = ({
  subjectSchemaId,
  addRelatedResourceCondition,
  disabled = false,
}) => {
  const { schemaCatalog, getSchemaById } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const relationshipOptions = subjectSchemaId
    ? getRelationshipOptionsForSubject({ graph: schemaCatalog, subjectSchemaId })
    : [];

  const selectedSubjectName = subjectSchemaId
    ? (getSchemaById(subjectSchemaId)?.name ?? 'record')
    : 'record';

  const buildOptionLabel = (relationship: SchemaRelationship): string => {
    if (!subjectSchemaId) return 'Related resource';
    const relationshipDirection = getRelationshipDirection({ relationship, subjectSchemaId });
    const { relatedSchemaId, linkedResourceOwnsForeignKey } = relationshipDirection;
    const relatedName = getSchemaById(relatedSchemaId)?.name ?? 'Related resource';
    const manyMarker = relationship.cardinality === 'many' ? ' []' : '';
    const directionHint = linkedResourceOwnsForeignKey
      ? `(references ${selectedSubjectName})`
      : `(${selectedSubjectName} references it)`;
    return `${relatedName} — via ${relationship.label}${manyMarker} ${directionHint}`;
  };

  const addConditionForRelationship = (relationship: SchemaRelationship) => {
    if (!subjectSchemaId) return;

    const { foreignKeyOn, relatedSchemaId } = getRelationshipDirection({
      relationship,
      subjectSchemaId,
    });
    addRelatedResourceCondition({
      id: generateId(),
      type: 'related_resource',
      schemaId: relatedSchemaId,
      schemaName: getSchemaById(relatedSchemaId)?.name ?? 'Related resource',
      foreignKeyPath: relationship.foreignKeyPath,
      foreignKeyOn,
      countOperator: CountOperator.GTE,
      countValue: 1,
      conditions: [],
      isExpanded: true,
    });
  };

  const handleRelationshipChange = subjectSchemaId
    ? (event: React.ChangeEvent<HTMLSelectElement>) => {
        const relationship = relationshipOptions.find(option => option.id === event.target.value);
        if (relationship) addConditionForRelationship(relationship);
      }
    : undefined;

  return (
    <select
      className={styles.addRelatedResourceSelect}
      value=""
      onChange={handleRelationshipChange}
      disabled={disabled || relationshipOptions.length === 0}
      aria-label="Add related resource condition">
      <option value="">+ Add related resource...</option>
      {relationshipOptions.map(relationship => (
        <option key={relationship.id} value={relationship.id}>
          {buildOptionLabel(relationship)}
        </option>
      ))}
    </select>
  );
};
